import React, { useEffect, useState } from 'react';
import api, { BASE as API_BASE } from '../services/api';
import { toast } from '../utils/toastBus';
import ProjectSetup from '../components/onboarding/ProjectSetup';
import RouteCarousel from '../components/onboarding/RouteCarousel';
import GatingModal from '../components/GatingModal';
import '../styles/onboarding.css';

export default function Onboarding() {
  const [sessionId, setSessionId] = useState('');
  const [brandName, setBrandName] = useState('');
  const [project, setProject] = useState('');
  const [goals, setGoals] = useState('awareness');
  const [demographics, setDemographics] = useState('18-34, US');
  const [projectId, setProjectId] = useState('');
  const [routes, setRoutes] = useState([]);
  const [estimates, setEstimates] = useState([]);
  const [selectedRoute, setSelectedRoute] = useState(0);
  const [introProgress, setIntroProgress] = useState({ progress: 0, phase: 'waiting', message: 'Ready to start' });
  const [videoProgress, setVideoProgress] = useState({ progress: 0, phase: 'waiting', message: 'Ready to start' });
  const [introResult, setIntroResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pred, setPred] = useState({ id: '', status: '' });
  const [error, setError] = useState('');
  const [videoMode, setVideoMode] = useState('static'); // 'static' | 'motion' | 'animated'
  const [isComposing, setIsComposing] = useState(false);
  const [isGeneratingVideo, setIsGeneratingVideo] = useState(false);
  const [introLen, setIntroLen] = useState(5);
  const [coreLen, setCoreLen] = useState(8);
  const [outroLen, setOutroLen] = useState(5);
  const [showGate, setShowGate] = useState(false);
  // Credits and estimates
  const [myCredits, setMyCredits] = useState({ balance: 0 });

  // Load current user's credits once
  useEffect(() => {
    (async () => {
      try {
        const me = await api.getMyCredits();
        const balance = Number(me?.balance ?? me?.minutes ?? 0) || 0;
        setMyCredits({ balance });
      } catch {}
    })();
  }, []);

  async function createSession() {
    setError('');
    setBusy(true);
    try {
      if (!String(project || '').trim()) {
        setBusy(false);
        setError('project_required');
        try { toast({ type: 'error', message: 'Project name is required' }); } catch {}
        return;
      }

      const brandPayload = brandName ? { brand: { name: brandName } } : {};
      const r = await api.onboardingCreateSession({ ...brandPayload, project, goals: String(goals).split(',').map(s=>s.trim()).filter(Boolean), demographics });
      if (r?.sessionId) setSessionId(r.sessionId);
      try { await api.trackPageview({ path: '/onboarding/session/created' }); } catch {}
    } catch (e) { setError('failed_to_create_session'); }
    setBusy(false);
  }

  // Progress polling for intro composition
  useEffect(() => {
    if (!isComposing || !sessionId) return;

    const pollInterval = setInterval(async () => {
      try {
        const status = await api.onboardingSessionStatus(sessionId);
        if (status?.ok && status?.progress) {
          setIntroProgress(status.progress);
          if (status.progress.phase === 'complete' || status.progress.phase === 'error') {
            setIsComposing(false);
            if (status.progress.phase === 'complete') {
              // Refresh intro result
              const session = status.session;
              if (session?.assets?.intro) {
                const assets = session.assets.intro;
                const norm = (u) => (u && typeof u === 'string' && u.startsWith('/uploads/') ? `${API_BASE}${u}` : u);
                setIntroResult({
                  assets: {
                    imageUrl: norm(assets.imageUrl),
                    audioPath: norm(assets.audioPath)
                  }
                });
              }
            }
          }
        }
      } catch (e) {
        console.error('Failed to poll intro progress:', e);
      }
    }, 2000);

    return () => clearInterval(pollInterval);
  }, [isComposing, sessionId]);

  // Progress polling for video generation
  useEffect(() => {
    if (!isGeneratingVideo || !sessionId) return;

    const pollInterval = setInterval(async () => {
      try {
        const status = await api.onboardingSessionStatus(sessionId);
        if (status?.ok && status?.progress) {
          setVideoProgress(status.progress);
          if (status.progress.phase === 'complete' || status.progress.phase === 'error') {
            setIsGeneratingVideo(false);
            if (status.progress.phase === 'complete') {
              // Refresh video result
              const session = status.session;
              if (session?.assets?.main) {
                const assets = session.assets.main;
                const norm = (u) => (u && typeof u === 'string' && u.startsWith('/uploads/') ? `${API_BASE}${u}` : u);
                setPred({
                  id: pred.id,
                  status: 'completed',
                  videoUrl: norm(assets.videoUrl),
                  thumbnailUrl: norm(assets.thumbnailUrl)
                });
              }
            }
          }
        }
      } catch (e) {
        console.error('Failed to poll video progress:', e);
      }
    }, 3000);

    return () => clearInterval(pollInterval);
  }, [isGeneratingVideo, sessionId]);

  // Auto-finalize when video is ready
  useEffect(() => {
    if (!pred.id || pred.status === 'completed' || !sessionId) return;

    const checkAndFinalize = async () => {
      try {
        // Check if video is ready by calling video status
        const statusResponse = await api.adVideoStatus(pred.id);
        if (statusResponse?.status === 'succeeded' && statusResponse?.output) {
          // Video is ready, call finalize
          try {
            const r = routes[selectedRoute] || routes[0] || {};
            const img = introResult?.assets?.imageUrl || '';
            const aud = introResult?.assets?.audioPath || '';
            const imageUrl = img && img.startsWith('/uploads/') ? `${API_BASE}${img}` : img;
            const audioPath = aud && aud.startsWith('/uploads/') ? `${API_BASE}${aud}` : aud;
            const aspectRatio = '16:9';
            const durationSec = coreLen || r?.main?.lengthSec || 8;
            const sfx = r?.main?.sfx || [];
            const finalizeResponse = await api.onboardingVideoFinalize({ sessionId, predictionId: pred.id, audioPath, imageUrl, aspectRatio, durationSec, sfx });
            if (finalizeResponse?.videoUrl) {
              setPred({
                id: pred.id,
                status: 'completed',
                videoUrl: finalizeResponse.videoUrl,
                thumbnailUrl: finalizeResponse.thumbnailUrl
              });
            }
          } catch (e) {
            console.error('Auto-finalize failed:', e);
          }
        }
      } catch (e) {
        console.error('Auto-finalize check failed:', e);
      }
    };

    // Check every 5 seconds after video starts
    const autoFinalizeInterval = setInterval(checkAndFinalize, 5000);
    return () => clearInterval(autoFinalizeInterval);
  }, [pred.id, pred.status, sessionId]);

  async function genRoutes() {
    if (!sessionId) return;
    setError('');
    setBusy(true);
    try {
      const r = await api.onboardingGeneratePrompts({ sessionId, preferences: { lengths: { intro: introLen, main: coreLen, outro: outroLen } } });
      if (Array.isArray(r?.routes)) setRoutes(r.routes);
      if (Array.isArray(r?.estimates)) setEstimates(r.estimates);
      try { await api.trackPageview({ path: '/onboarding/routes/success' }); } catch {}
    } catch (e) { setError('failed_to_generate'); }
    setBusy(false);
  }

  async function composeIntro() {
    if (!sessionId || !routes.length) return;
    setError('');
    setBusy(true);
    setIsComposing(true);
    setIntroProgress({ progress: 0, phase: 'generating', message: 'Starting generation...' });
    try {
      const r = routes[selectedRoute] || routes[0];
      const res = await api.onboardingCompose({ sessionId, segment: 'intro', imagePrompt: r.intro.imagePrompt, voPrompt: r.intro.voPrompt, sfx: r.intro.sfx, lengthSec: r.intro.lengthSec });
      const assets = res?.assets || {};
      const norm = (u) => (u && typeof u === 'string' && u.startsWith('/uploads/') ? `${API_BASE}${u}` : u);
      const normalized = res ? { ...res, assets: { ...assets, imageUrl: norm(assets.imageUrl), audioPath: norm(assets.audioPath) } } : null;
      setIntroResult(normalized);
      try { await api.trackPageview({ path: '/onboarding/intro/success' }); } catch {}
      // Append assets to project if available
      try {
        if (projectId) {
          if (normalized?.assets?.imageUrl) {
            await api.appendProjectAsset(projectId, { type: 'intro_image', url: normalized.assets.imageUrl, status: 'draft' });
          }
          if (normalized?.assets?.audioPath) {
            await api.appendProjectAsset(projectId, { type: 'intro_audio', url: normalized.assets.audioPath, status: 'draft', meta: { segment: 'intro' } });
          }
        }
      } catch {}
    } catch (e) { setError('compose_failed'); setIntroProgress({ progress: 0, phase: 'error', message: 'Generation failed' }); try { await api.trackPageview({ path: '/onboarding/intro/error' }); } catch {} }
    setBusy(false);
    setIsComposing(false);
  }

  async function startVideo() {
    if (!sessionId || !routes.length) return;
    setError('');
    setBusy(true);
    setIsGeneratingVideo(true);
    setVideoProgress({ progress: 0, phase: 'video', message: 'Starting video generation...' });
    try {
      const r = routes[selectedRoute] || routes[0];
      // Auto-pick mode based on prompts and available assets
      function autoPickVideoMode(route) {
        try {
          const vp = String(route?.main?.videoPrompt || '').toLowerCase();
          const hasVideoPrompt = vp.length > 0;
          const hasIntroImage = Boolean(introResult?.assets?.imageUrl);
          if (hasVideoPrompt) return 'animated';
          if (hasIntroImage) return 'motion';
          return 'static';
        } catch { return 'static'; }
      }
      const pickedMode = autoPickVideoMode(r);
      if (pickedMode !== videoMode) setVideoMode(pickedMode);
      const img = introResult?.assets?.imageUrl || '';
      const aud = introResult?.assets?.audioPath || '';
      const imageUrl = img && img.startsWith('/uploads/') ? `${API_BASE}${img}` : img;
      const audioPath = aud && aud.startsWith('/uploads/') ? `${API_BASE}${aud}` : aud;
      // Requirements per mode
      if (videoMode === 'animated') {
        if (!audioPath) { setError('intro_assets_required'); setBusy(false); return; }
      } else {
        if (!imageUrl || !audioPath) { setError('intro_assets_required'); setBusy(false); return; }
      }
      const start = await api.onboardingVideoStart({ sessionId, aspectRatio: '16:9', durationSec: coreLen || r.main.lengthSec, prompt: r.main.videoPrompt, sfx: r.main.sfx, mode: pickedMode, resolution: r.main.resolution || '1080p', imageUrl, audioPath });
      if (start?.error === 'payment_required') {
        setError(`payment_required: need ~${start.estimateCredits}, balance ${start.balance}`);
        setShowGate(true);
        setBusy(false);
        setIsGeneratingVideo(false);
        try { await api.trackPageview({ path: '/onboarding/video/payment_required' }); } catch {}
        return;
      }
      if (start?.error) {
        setError(String(start.error));
        setIsGeneratingVideo(false);
        try { await api.trackPageview({ path: '/onboarding/video/error' }); } catch {}
      } else if (start?.predictionId) {
        setPred({ id: start.predictionId, status: 'started' });
        try { await api.trackPageview({ path: '/onboarding/video/start' }); } catch {}
      } else {
        setError('video_start_failed');
        setIsGeneratingVideo(false);
        try { await api.trackPageview({ path: '/onboarding/video/error' }); } catch {}
      }
    } catch (e) { setError('video_start_failed'); setVideoProgress({ progress: 0, phase: 'error', message: 'Video generation failed' }); setIsGeneratingVideo(false); }
    setBusy(false);
  }

  async function finalizeVideo(opts = {}) {
    if (!sessionId || !pred.id) return;
    setError('');
    setBusy(true);
    try {
      const r = routes[selectedRoute] || routes[0] || {};
      const img = introResult?.assets?.imageUrl || '';
      const aud = introResult?.assets?.audioPath || '';
      const imageUrl = img && img.startsWith('/uploads/') ? `${API_BASE}${img}` : img;
      const audioPath = aud && aud.startsWith('/uploads/') ? `${API_BASE}${aud}` : aud;
      const aspectRatio = '16:9';
      const durationSec = coreLen || r?.main?.lengthSec || 8;
      const sfx = r?.main?.sfx || [];
      const fin = await api.onboardingVideoFinalize({ sessionId, predictionId: pred.id, audioPath, imageUrl, aspectRatio, durationSec, sfx, ...(opts?.simple ? { simple: true } : {}) });
      if (fin?.videoUrl) setPred({ id: pred.id, status: 'completed', videoUrl: fin.videoUrl, thumbnailUrl: fin.thumbnailUrl });
      try { await api.trackPageview({ path: '/onboarding/video/finalize' }); } catch {}
      // Append video/thumbnail assets to project if available
      try {
        if (projectId) {
          if (fin?.videoUrl) {
            const vurl = fin.videoUrl && fin.videoUrl.startsWith('/uploads/') ? `${API_BASE}${fin.videoUrl}` : fin.videoUrl;
            await api.appendProjectAsset(projectId, { type: 'video', url: vurl, status: 'draft', meta: { predictionId: pred.id } });
          }
          if (fin?.thumbnailUrl) {
            const turl = fin.thumbnailUrl && fin.thumbnailUrl.startsWith('/uploads/') ? `${API_BASE}${fin.thumbnailUrl}` : fin.thumbnailUrl;
            await api.appendProjectAsset(projectId, { type: 'thumb', url: turl, status: 'draft' });
          }
        }
      } catch {}
    } catch (e) { setError('video_finalize_failed'); try { await api.trackPageview({ path: '/onboarding/video/finalize_error' }); } catch {} }
    setBusy(false);
  }

  const canGenerate = project && !sessionId;
  const canRoutes = !!sessionId;
  const canComposeIntro = !!sessionId && routes.length > 0;

  return (
    <div style={{ padding: 24 }}>
      <h1 style={{ margin: '8px 0' }}>Onboarding</h1>
      {error && <div style={{ color: '#fca5a5', marginBottom: 12 }}>{String(error)}</div>}

      <section style={{ marginBottom: 24 }}>
        <h2 style={{ margin: '8px 0' }}>1. Project Setup</h2>
        <div style={{ display: 'grid', gap: 12, maxWidth: 760 }}>
          <ProjectSetup onCreated={(pid) => { try { setProjectId(pid); setProject(prev => prev || `Project ${pid.slice(0,8)}`); createSession(); } catch {} }} />
          {sessionId && <div style={{ color: '#9ca3af' }}>Session: {sessionId}</div>}
          <GatingModal
        open={showGate}
        onClose={() => setShowGate(false)}
        onSignup={() => { try { window.location.href = '/register'; } catch {} }}
        onLogin={() => { try { window.location.href = '/login'; } catch {} }}
      />
    </div>
      </section>

      <section style={{ marginBottom: 24 }}>
        <h2 style={{ margin: '8px 0' }}>2. Generate Routes</h2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <button disabled={!canRoutes || busy} onClick={genRoutes} style={{ background: '#ff5a1f', color: '#fff', border: 0, padding: '8px 12px', borderRadius: 6, cursor: 'pointer' }}>Generate</button>
          {!!routes.length && <span style={{ color: '#9ca3af' }}>Routes: {routes.length}</span>}
        </div>
        <div style={{ marginTop: 10, display: 'grid', gap: 8, maxWidth: 680 }}>
          <label style={{ color: '#e5e7eb' }}>Intro length: {introLen}s
            <input type="range" min={5} max={15} value={introLen} onChange={(e)=>setIntroLen(Number(e.target.value))} style={{ width: '100%' }} />
          </label>
          <label style={{ color: '#e5e7eb' }}>Core length: {coreLen}s
            <input type="range" min={5} max={15} value={coreLen} onChange={(e)=>setCoreLen(Number(e.target.value))} style={{ width: '100%' }} />
          </label>
          <label style={{ color: '#e5e7eb' }}>Outro length: {outroLen}s
            <input type="range" min={5} max={15} value={outroLen} onChange={(e)=>setOutroLen(Number(e.target.value))} style={{ width: '100%' }} />
          </label>
          <div style={{ color: '#9ca3af' }}>Total: {introLen + coreLen + outroLen}s</div>
        </div>
        {!!routes.length && (
          <div style={{ marginTop: 12 }}>
            <RouteCarousel
              routes={routes}
              estimates={estimates}
              selectedIndex={selectedRoute}
              onSelect={setSelectedRoute}
              introLen={introLen}
              coreLen={coreLen}
              outroLen={outroLen}
              setIntroLen={setIntroLen}
              setCoreLen={setCoreLen}
              setOutroLen={setOutroLen}
              onGenerateIntro={composeIntro}
              onStartVideo={startVideo}
              onSaveRoute={async (payload) => {
                try {
                  if (!projectId) return;
                  const body = {
                    ...payload,
                    projectName: project || brandName || 'your brand',
                    goals,
                    demographics,
                  };
                  const res = await api.saveProjectRoute(projectId, body);
                  return res;
                } catch {}
              }}
            />
          </div>
        )}
      </section>

      <section style={{ marginBottom: 24 }}>
        <h2 style={{ margin: '8px 0' }}>2.5 Free Intro (Image + VO + SFX)</h2>
        <div style={{ display: 'grid', gap: 8, alignItems: 'start', maxWidth: 680 }}>
          <button disabled={!canComposeIntro || busy} onClick={composeIntro} style={{ background: '#ff5a1f', color: '#fff', border: 0, padding: '8px 12px', borderRadius: 6, cursor: 'pointer' }}>Generate Intro</button>

          {/* Progress Display for Intro */}
          {isComposing && (
            <div style={{ padding: '12px', background: '#1f2937', borderRadius: 8, border: '1px solid #374151' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                <div style={{ color: '#fbbf24', fontSize: 14 }}>⏳</div>
                <div>
                  <div style={{ color: '#e5e7eb', fontSize: 14, fontWeight: 500 }}>{introProgress.message}</div>
                  <div style={{ color: '#9ca3af', fontSize: 12 }}>Phase: {introProgress.phase} • Progress: {introProgress.progress}%</div>
                </div>
              </div>
              <div style={{ background: '#374151', height: 4, borderRadius: 2, overflow: 'hidden' }}>
                <div style={{
                  height: '100%',
                  width: `${introProgress.progress}%`,
                  background: 'linear-gradient(90deg, #ff5a1f, #00e8ff)',
                  borderRadius: 2,
                  transition: 'width 0.5s ease'
                }}></div>
              </div>
              {introProgress.estimatedTotal && (
                <div style={{ color: '#9ca3af', fontSize: 12, marginTop: 4 }}>
                  Estimated time: ~{Math.ceil(introProgress.estimatedTotal / 60)} minutes
                </div>
              )}
            </div>
          )}

          {(() => {
            try {
              const img = introResult?.assets?.imageUrl;
              const src = img && img.startsWith('/uploads/') ? `${API_BASE}${img}` : img;
              return src ? (
                <div>
                  <div style={{ color: '#9ca3af', fontSize: 12, marginBottom: 4 }}>Intro Image</div>
                  <img alt="intro" src={src} style={{ height: 120, borderRadius: 8, border: '1px solid #374151' }} />
                </div>
              ) : null;
            } catch { return null; }
          })()}
          {(() => {
            try {
              const aud = introResult?.assets?.audioPath;
              const src = aud && aud.startsWith('/uploads/') ? `${API_BASE}${aud}` : aud;
              return src ? (
                <div>
                  <div style={{ color: '#9ca3af', fontSize: 12, marginBottom: 4 }}>Intro Voiceover + SFX</div>
                  <audio controls preload="metadata" style={{ width: '100%' }} src={src} />
                  <div style={{ marginTop: 6 }}>
                    <a href={src} target="_blank" rel="noreferrer" style={{ color: '#93c5fd' }}>Open audio</a>
                    <span style={{ color: '#4b5563' }}> • </span>
                    <a href={src} download style={{ color: '#93c5fd' }}>Download</a>
                  </div>
                </div>
              ) : null;
            } catch { return null; }
          })()}
        </div>
      </section>

      <section>
        <h2 style={{ margin: '8px 0' }}>3. Full Video (Gated)</h2>
        {/* Credits estimate banner */}
        {(() => {
          try {
            const est = Array.isArray(estimates) && estimates.length ? (estimates[selectedRoute] || estimates[0]) : null;
            const mainEst = est?.main ?? null;
            const totalEst = est?.total ?? null;
            const bal = Number(myCredits?.balance || 0);
            if (mainEst == null && totalEst == null) return null;
            const insufficient = Number.isFinite(mainEst) && bal < mainEst;
            return (
              <div style={{
                marginBottom: 12,
                padding: '10px 12px',
                borderRadius: 8,
                border: `1px solid ${insufficient ? 'rgba(245, 158, 11, 0.35)' : 'rgba(16, 185, 129, 0.35)'}`,
                background: insufficient ? 'rgba(245, 158, 11, 0.08)' : 'rgba(16, 185, 129, 0.08)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ color: insufficient ? '#f59e0b' : '#10b981', fontWeight: 600 }}>
                    {insufficient ? 'Estimated credits may exceed your balance' : 'Estimated credits' }
                  </div>
                  <div style={{ color: '#9ca3af' }}>
                    Main: <strong style={{ color: '#e5e7eb' }}>{mainEst ?? '—'}</strong>
                    <span style={{ color: '#4b5563' }}> • </span>
                    Total (intro+main+outro): <strong style={{ color: '#e5e7eb' }}>{totalEst ?? '—'}</strong>
                    <span style={{ color: '#4b5563' }}> • </span>
                    Balance: <strong style={{ color: '#e5e7eb' }}>{bal}</strong>
                  </div>
                </div>
              </div>
            );
          } catch { return null; }
        })()}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ color: '#e5e7eb', display: 'flex', alignItems: 'center', gap: 8 }}>
            Mode
            <select value={videoMode} onChange={(e) => setVideoMode(e.target.value)} style={{ padding: 8, borderRadius: 8, border: '1px solid #374151', background: '#0b0b0e', color: '#e5e7eb' }}>
              <option value="static">Static (image + audio)</option>
              <option value="motion">Motion (image → video)</option>
              <option value="animated">Animated (prompt → video)</option>
            </select>
          </label>
          <button disabled={!routes.length || busy} onClick={startVideo} style={{ background: '#ff5a1f', color: '#fff', border: 0, padding: '8px 12px', borderRadius: 6, cursor: 'pointer' }}>Start Video</button>
          <button disabled={!pred.id || busy} onClick={() => finalizeVideo()} style={{ background: '#111827', color: '#fff', border: '1px solid #374151', padding: '8px 12px', borderRadius: 6, cursor: 'pointer' }}>Finalize</button>
          <button disabled={!pred.id || busy} onClick={() => finalizeVideo({ simple: true })} title="Finalize without overlays/SFX (fallback)" style={{ background: '#374151', color: '#fff', border: 0, padding: '8px 12px', borderRadius: 6, cursor: 'pointer' }}>Try Simple Finalize</button>
          <button
            disabled={busy || !introResult?.assets?.audioPath}
            onClick={async () => {
              try {
                setPred({ id: '', status: 'skipped' });
                try { await api.trackPageview({ path: '/onboarding/video/skipped' }); } catch {}
                try { toast({ type: 'info', message: 'Skipping video. Intro assets saved.' }); } catch {}
              } catch {}
            }}
            style={{ background: '#374151', color: '#fff', border: 0, padding: '8px 12px', borderRadius: 6, cursor: 'pointer' }}
          >Skip Video</button>
          {pred?.status && <span style={{ color: '#9ca3af' }}>Status: {pred.status}</span>}
        </div>

        {/* Progress Display for Video */}
        {isGeneratingVideo && (
          <div style={{ padding: '12px', background: '#1f2937', borderRadius: 8, border: '1px solid #374151', marginTop: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
              <div style={{ color: '#60a5fa', fontSize: 14 }}>🎬</div>
              <div>
                <div style={{ color: '#e5e7eb', fontSize: 14, fontWeight: 500 }}>{videoProgress.message}</div>
                <div style={{ color: '#9ca3af', fontSize: 12 }}>Phase: {videoProgress.phase} • Progress: {videoProgress.progress}%</div>
              </div>
            </div>
            <div style={{ background: '#374151', height: 4, borderRadius: 2, overflow: 'hidden' }}>
              <div style={{
                height: '100%',
                width: `${videoProgress.progress}%`,
                background: 'linear-gradient(90deg, #3b82f6, #6366f1)',
                borderRadius: 2,
                transition: 'width 0.5s ease'
              }}></div>
            </div>
            {videoProgress.estimatedTotal && (
              <div style={{ color: '#9ca3af', fontSize: 12, marginTop: 4 }}>
                Estimated time: ~{Math.ceil(videoProgress.estimatedTotal / 60)} minutes
              </div>
            )}
          </div>
        )}

        {pred?.videoUrl && (
          <video src={pred.videoUrl} controls style={{ marginTop: 12, maxWidth: '100%', borderRadius: 8, border: '1px solid #374151' }} />
        )}
      </section>
    </div>
  );
}
